import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import logoImage from "../Assets/logoImage.webp";

export default function Header() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // ✅ useMemo: navItems will not re-create on every render
  const navItems = useMemo(
    () => [
      { label: "Home", path: "/" },
      { label: "Features", path: "/features" },
      { label: "Pricing", path: "/pricing" },
      { label: "About Us", path: "/about-us" },
      { label: "Contact", path: "/contact-us" },
    ],
    []
  );

  // ✅ useCallback: Avoid re-creation on every render
  const handleToggleMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;

    script.onload = () => {
      console.log("✅ Calendly script loaded");
    };

    script.onerror = () => {
      console.error("❌ Failed to load Calendly script");
    };

    document.body.appendChild(script);

    const handleEvent = (e) => {
      if (e?.data?.event === "calendly.event_scheduled") {
        console.log("✅ Meeting Scheduled:", e.data);
      }
    };

    window.addEventListener("message", handleEvent);
    return () => window.removeEventListener("message", handleEvent);
  }, []);

  // ✅ Popup handler with fallback retry
  const handleBookDemo = () => {
    console.log("👆 Book Demo clicked");

    const openPopup = () => {
      if (window.Calendly?.initPopupWidget) {
        window.Calendly.initPopupWidget({
          url: "https://calendly.com/triosoft-support/30min",
        });
        console.log("✅ Calendly Popup Opened");
        return true;
      }
      return false;
    };

    // ✅ Try opening immediately
    if (openPopup()) return;

    // ✅ Retry after delay if script isn't ready
    console.warn("⚠️ Calendly not ready, retrying...");
    setTimeout(() => {
      if (!openPopup()) {
        alert("Calendly is still loading. Please try again in a moment.");
      }
    }, 500);
  };

  return (
    <header
      className="mx-auto sticky top-0 z-50 w-full border-b bg-[var(--white)] shadow-sm"
      aria-label="Main navigation"
    >
      <div className="max-w-[1400px] mx-auto px-4 lg:px-5 xl:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* ✅ Corrected Link syntax for react-router-dom */}
          <Link
            to="/"
            title="Go to MaalWahak Home"
            className="flex items-center gap-2"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <img
                src={logoImage}
                alt="MaalWahak Company Logo"
                title="MaalWahak - Smart Transport Management"
                loading="lazy"
                decoding="async"
                className="h-10 w-auto select-none"
              />
            </motion.div>
          </Link>

          {/* ✅ Desktop Nav */}
          <nav
            className="hidden items-center gap-6 lg:flex"
            aria-label="Primary navigation"
          >
            {navItems.map((item, index) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  title={`Go to ${item.label} page`}
                >
                  <motion.span
                    className={`text-sm font-medium transition-colors rounded-md px-3 py-2 inline-block ${
                      isActive
                        ? "text-[--primary-color]"
                        : "text-foreground hover:text-[--primary-color]"
                    }`}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.08 }}
                    whileHover={{ y: -2 }}
                  >
                    {item.label}
                  </motion.span>
                </Link>
              );
            })}
          </nav>

          {/* ✅ Buttons */}
          <div className="flex items-center gap-3">
            <a
              href="https://api.maalwahak.com/Welcome/login_s"
              target="_blank"
              rel="noopener noreferrer"
              title="Login to MaalWahak Portal"
              className="hidden sm:block"
            >
              <button
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm font-medium hover:bg-gray-50 transition"
                style={{ height: "36px" }}
              >
                Login
              </button>
            </a>

            {/* <Link
              to="https://api.maalwahak.com/Welcome/login_s"
              title="Start your free trial"
            > */}
            <button
              className="theme-btn ml-2 text-sm font-semibold"
              onClick={handleBookDemo}
            >
              Book a Demo
            </button>
            {/* </Link> */}

            <button
              onClick={handleToggleMenu}
              aria-label="Toggle mobile menu"
              aria-expanded={mobileMenuOpen}
              className="lg:hidden border rounded-md p-2 hover:bg-gray-50 transition"
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* ✅ Mobile Nav */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              className="border-t py-4 lg:hidden bg-white"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <nav
                className="flex flex-col gap-2"
                aria-label="Mobile navigation"
              >
                {navItems.map((item, index) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    title={`Navigate to ${item.label}`}
                    onClick={handleCloseMenu}
                  >
                    <motion.span
                      className={`block rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                        location.pathname === item.path
                          ? "text-[--primary-color]"
                          : "text-foreground hover:text-[--primary-color]"
                      }`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      {item.label}
                    </motion.span>
                  </Link>
                ))}
                <a
                  href="https://api.maalwahak.com/Welcome/login_s"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Login to MaalWahak"
                >
                  <button className="w-full text-left px-4 py-2 text-sm font-medium border-t mt-2 hover:bg-gray-50 transition">
                    Login
                  </button>
                </a>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
